<template>
  <div class="input-checkbox" :class="classes">
    <cv-checkbox
      :id="id"
      :mixed="mixed"
      :value="value"
      :disabled="disabled"
      :checked="isChecked"
      :label="value"
      :aria-label="label"
      :hide-label="hideLabel"
      @change="handleChange"
    />
    <label :for="id">
      <!-- Avoid usage of "icon" as class here as it will be affected by
           generic styling for icon across the system -->
      <span class="input-icon">
        <svg-icon :name="icon" kind="forms" />
      </span>
      <span class="text">
        <slot></slot>
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";

type Kind = "grey" | "white";

const props = defineProps<{
  id: string;
  mixed?: boolean;
  disabled?: boolean;
  value: string;
  selectedValues?: string[] | string | null;
  kind?: Kind;
  hideLabel?: boolean;
  label?: string;
}>();

const emit = defineEmits<{
  (e: "update:selectedValues", value: string[]): void;
  (e: "change", value: string[]): void;
}>();

const selectedValuesInner = ref<string[]>([]);

const kind = computed(() => props.kind ?? "grey");
const hasSelectedValues = computed(
  () => props.selectedValues !== undefined && props.selectedValues !== null,
);

watch(
  () => props.selectedValues,
  (newVal) => {
    if (hasSelectedValues.value) {
      selectedValuesInner.value = Array.isArray(newVal) ? [...newVal] : [];
    }
  },
  { deep: true },
);

const isChecked = computed(() =>
  (hasSelectedValues.value
    ? Array.isArray(props.selectedValues)
      ? props.selectedValues
      : []
    : selectedValuesInner.value
  ).includes(props.value),
);

function handleChange(checked: boolean) {
  let updated: string[];
  if (hasSelectedValues.value) {
    updated = Array.isArray(props.selectedValues) ? [...props.selectedValues] : [];
  } else {
    updated = [...selectedValuesInner.value];
  }

  if (checked) {
    if (!updated.includes(props.value)) updated.push(props.value);
  } else {
    const index = updated.indexOf(props.value);
    if (index !== -1) updated.splice(index, 1);
  }

  if (hasSelectedValues.value) {
    emit("update:selectedValues", updated);
  } else {
    selectedValuesInner.value = updated;
  }

  emit("change", updated);
}

const classes = computed(() => [
  `input-checkbox-${kind.value}`,
  {
    disabled: props.disabled,
    selected: isChecked.value,
  },
]);

const icon = computed(() => {
  if (isChecked.value) {
    return props.disabled ? "CheckBoxDisabled24" : "CheckBoxChecked24";
  } else if (props.mixed) {
    return "CheckBoxIndeterminate24";
  }
  return "CheckBox24";
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.input-checkbox {
  display: flex;
  align-items: center;
  margin: $spacing-05 0;
  word-break: break-word;
  border: 1px solid transparent;
  border-radius: 10px;

  &.input-checkbox-white {
    &:not(.selected) {
      .sls-icon:not([name="CheckBoxIndeterminate24"]) {
        rect {
          fill: $ui-01;
        }
      }
    }
  }

  &.disabled {
    cursor: not-allowed;
    &.selected {
      > label {
        > .input-icon {
          rect {
            stroke: $disabled-02;
            fill: $disabled-02;
          }
        }
      }
    }
    > label {
      color: $disabled-02;
      cursor: default;

      > .input-icon {
        cursor: not-allowed;
        rect {
          stroke: transparent;
        }
      }

      .rich-text-editor {
        color: $text-01;
      }

      // To allow scrolling of the text when there is scrollbar
      .text {
        pointer-events: all;
      }
    }
  }

  // Checkbox aligned top
  .bx--form-item.bx--checkbox-wrapper {
    flex: 0;
    margin-top: $spacing-01;
    margin-bottom: auto;

    // Hide carbon default checkbox to use our own customization
    > .bx--checkbox-label {
      display: none;
    }
  }

  > label {
    display: flex;
    // To align top intentionally according to designer instead of center
    align-items: flex-start;
    flex: 0 0 100%;
    max-width: 100%;
    cursor: pointer;

    > .input-icon {
      width: 24px;
      height: 24px;
      margin-right: $spacing-03;
    }

    .text {
      @include carbon--type-style("body-short-01");
      flex: 1 1 0%;
      margin-top: $spacing-01;
    }
  }
}
</style>
